import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, DialogActions, DialogContent, Grid, Typography } from '@mui/material';
import { Formik } from 'formik';
import { GlobalState } from 'reducers/stateTypes';
import { Employee, EmployeeRole, Partner } from 'types/Partner';
import ValidatedDropdown, { DropdownOption } from 'components/Common/ValidatedDropdown';
import { closeAllModals, openModal } from 'reducers/modalReducer';
import { LoadingButton } from '@mui/lab';
import ResponsiveDialog from 'components/Common/ResponsiveDialog';
import { updatePartnerRm } from 'services/partnerService';

const getRmOptions = (thRm: Employee[]) => {
  const filteredThRm =
    thRm?.filter(
      (employee: Employee) =>
        employee.roles.includes(EmployeeRole.TEAM_HEAD) || employee.roles.includes(EmployeeRole.RELATIONSHIP_MANAGER),
    ) || [];
  return filteredThRm?.map((employee) => {
    return { value: employee.userId.toLowerCase(), label: employee.fullName };
  });
};

const ReassignRmModal = ({ partner }: { partner: Partner }) => {
  const thRm = useSelector((state: GlobalState) => state.partnerForm.thRm);
  const rmOptions = getRmOptions(thRm);
  const originalRm = rmOptions.find((rm: DropdownOption) => rm.value === partner?.relationshipManager);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return (
    <ResponsiveDialog id="private-individual-creation-modal">
      <DialogContent>
        <Typography variant="h3">Reassign prospect</Typography>

        <Formik
          initialValues={{
            originalRm: originalRm?.value,
            newRm: '',
          }}
          onSubmit={async (data, { setSubmitting }) => {
            try {
              await updatePartnerRm(partner.partnerType, partner.techId, data.newRm);
              setSubmitting(false);
              dispatch(closeAllModals());
              window.location.reload();
            } catch (error) {
              dispatch(
                openModal({
                  modalType: 'JBErrorModalWrapper',
                  modalProps: {
                    technicalInfo: {
                      details: 'Error reassigning RM in partner-data-mgmt',
                      title: 'Error reassigning RM',
                      userErrorMessage: 'An error occurred when reassigning RM',
                    },
                    httpError: error,
                  },
                }),
              );
            }
          }}
        >
          {({ isSubmitting, submitForm }: { isSubmitting: boolean; submitForm: () => Promise<void> }) => {
            return (
              <>
                <Grid container spacing={2} sx={{ marginTop: '16px', marginBottom: '16px' }}>
                  <Grid item xs={6}>
                    <ValidatedDropdown
                      label={t('more.rm-reassign.currentRm')}
                      name="originalRm"
                      options={rmOptions}
                      disabled
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <ValidatedDropdown label={t('more.rm-reassign.newRm')} name="newRm" options={rmOptions} />
                  </Grid>
                </Grid>

                <DialogActions>
                  <Button
                    onClick={() => dispatch(closeAllModals())}
                    color="primary"
                    id="private-individual-cancel-button"
                  >
                    Cancel
                  </Button>

                  <LoadingButton
                    variant="contained"
                    color="primary"
                    type="submit"
                    loading={isSubmitting}
                    onClick={() => submitForm()}
                  >
                    Save
                  </LoadingButton>
                </DialogActions>
              </>
            );
          }}
        </Formik>
      </DialogContent>
    </ResponsiveDialog>
  );
};

export default ReassignRmModal;
